import React from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { ISorting } from "../../../interfaces/Components/ITable";
import type { IPagination2 } from "../../../interfaces/Components/ITable";
import type {
  CustomFilterApplyPayload,
  CustomFilterQueryClause,
  IFilterData,
  IGroupByConfig,
  IHeader,
} from "./FilteredTable.types";

export const SYNTHETIC_COLUMN_IDS = {
  SELECT: "select",
  INDEX: "index",
} as const;

export const FIELDS_TO_IGNORE = new Set([
  "id",
  "icon",
  "count",
  SYNTHETIC_COLUMN_IDS.INDEX,
  SYNTHETIC_COLUMN_IDS.SELECT,
  "edit",
  "actions",
  "subscribeCheck",
]);

export const COLUMN_VISIBILITY_PANEL_IDS = new Set([
  "index",
  "actions",
  SYNTHETIC_COLUMN_IDS.SELECT,
]);

export function isFilterHiddenFromUi(f: IFilterData): boolean {
  return f.type === "hidden" || f.hidden === true;
}

export function isSingleCustomFilterClause(
  clause: CustomFilterApplyPayload,
): clause is CustomFilterQueryClause {
  if (clause == null || typeof clause !== "object") {
    return false;
  }
  return "value" in clause;
}

export function getFilterPanelColClass(f: IFilterData): string {
  if (f.type === "radio") {
    return "col-span-12";
  }
  if (f.panelColSpan === "full") {
    return "col-span-12";
  }
  if (f.panelColSpan === "wide") {
    return "col-span-12 md:col-span-6 lg:col-span-4 2xl:col-span-3";
  }
  return "col-span-12 md:col-span-6 lg:col-span-3 2xl:col-span-2";
}

export function buildEmptyFilterValues(
  filtersData: IFilterData[],
  showSearchInput: boolean,
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  filtersData.forEach((f) => {
    if (isFilterHiddenFromUi(f)) {
      return;
    }
    if (f.type === "text" || f.type === "date") {
      next[f.name] = "";
    }
    else if (f.type === "custom") {
      next[f.name] =
        f.defaultCustomFilterValue !== undefined ? f.defaultCustomFilterValue : null;
    } else {
      next[f.name] = [];
    }
  });
  if (showSearchInput) {
    next.inputSearch = "";
  }
  next.quickFilter = null;
  return next;
}

export function collectCustomFilterDefaults(filtersData: IFilterData[]): {
  inputFilters: Record<string, unknown>;
  query: Record<string, unknown>;
} {
  const inputFilters: Record<string, unknown> = {};
  const query: Record<string, unknown> = {};

  filtersData.forEach((fc) => {
    if (isFilterHiddenFromUi(fc) || fc.type !== "custom") {
      return;
    }
    if (fc.defaultCustomFilterValue !== undefined) {
      inputFilters[fc.name] = fc.defaultCustomFilterValue;
    }
    if (fc.getDefaultQueryClauses) {
      const clauses = fc.getDefaultQueryClauses();
      if (clauses) {
        Object.assign(query, clauses);
      }
    } else if (fc.getDefaultQueryClause && fc.fieldName) {
      const clause = fc.getDefaultQueryClause();
      if (clause) {
        query[fc.fieldName] = clause;
      }
    }
  });

  return { inputFilters, query };
}

export function applyCustomFilterClausesToQuery(
  next: Record<string, unknown>,
  apiFieldName: string | undefined,
  clause: CustomFilterApplyPayload,
  customQueryFieldNames?: string[],
): void {
  const clearManagedFields = () => {
    if (customQueryFieldNames?.length) {
      customQueryFieldNames.forEach((field) => {
        delete next[field];
      });
    } else if (apiFieldName) {
      delete next[apiFieldName];
    }
  };

  if (clause == null) {
    clearManagedFields();
    return;
  }

  if (isSingleCustomFilterClause(clause)) {
    if (!apiFieldName) {
      return;
    }
    next[apiFieldName] = clause;
    return;
  }

  clearManagedFields();
  Object.entries(clause).forEach(([field, value]) => {
    next[field] = value;
  });
}

export function getGlobalRowNumber(
  rowIndex: number,
  pagination?: IPagination2
): number {
  if (!pagination) {
    return rowIndex + 1;
  }
  return rowIndex + 1 + pagination.pageIndex * pagination.pageSize;
}

export function safeCellValue(value: any): React.ReactNode {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

export function safeExcelValue(value: any): string | number | boolean {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return value;
}

export function buildExcelFilename(base?: string): string {
  const currentDate = new Date();
  const formattedDate = `${String(currentDate.getMonth() + 1).padStart(
    2,
    "0"
  )}_${String(currentDate.getDate()).padStart(
    2,
    "0"
  )}_${currentDate.getFullYear()}`;
  const safeBase = (base || "export")
    .replace(/^\/+/, "")
    .replace(/\?.*$/, "")
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${safeBase || "export"}_${formattedDate}.xlsx`;
}

export function nextSort(current: ISorting, field: string): ISorting {
  if (current.field !== field) {
    return { field, order: "asc" };
  }
  return { field, order: current.order === "asc" ? "desc" : "asc" };
}

export function getHeaderTitle(
  header: string | React.ReactNode,
  id: string,
  headerTooltip?: string,
): string {
  if (headerTooltip) {
    return headerTooltip;
  }
  if (typeof header === "string") {
    return header;
  }
  return id;
}

/** Excel export title: prefixes grouped columns with the group label */
export function getExcelColumnTitle(header: IHeader): string {
  const leaf = getHeaderTitle(header.header, header.id, header.headerTooltip);
  const groupLabel =
    header.headerGroupTooltip ??
    (typeof header.headerGroup === "string" ? header.headerGroup : "");
  if (!groupLabel) {
    return leaf;
  }
  return `${groupLabel} ${leaf}`;
}

export function getVisibleExcelColumns(
  columnsDef: IHeader[],
  excelHeaders: string[] | undefined,
  columnVisibility: Record<string, boolean>,
  currentData: boolean
): IHeader[] {
  const titlesToExport =
    excelHeaders ?? columnsDef.map((h) => getExcelColumnTitle(h));
  const titleToHeader = new Map(
    columnsDef.map((h) => [getExcelColumnTitle(h), h] as const),
  );

  const explicitExcelHeaders = excelHeaders !== undefined;

  return titlesToExport
    .map((title) => titleToHeader.get(title))
    .filter(Boolean)
    .filter((h) => !FIELDS_TO_IGNORE.has((h as IHeader).id))
    .filter((h) => {
      if (!currentData) {
        return true;
      }
      const header = h as IHeader;
      if (
        explicitExcelHeaders &&
        excelHeaders!.includes(getExcelColumnTitle(header))
      ) {
        return true;
      }
      return columnVisibility[header.id] !== false;
    }) as IHeader[];
}

/** IDs de columnas ancladas al inicio: prefijo contiguo de `columnsDef` con `columnOrderFixed`. */
export function getFixedColumnPrefixIds(columnsDef: IHeader[]): string[] {
  const ids: string[] = [];
  for (const h of columnsDef) {
    if (h.columnOrderFixed) {
      ids.push(h.id);
    }
    else {
      break;
    }
  }
  return ids;
}

/** Garantiza el prefijo fijo al inicio (orden de definición) y conserva el orden relativo del resto. */
export function normalizeColumnOrder(
  order: string[],
  columnsDef: IHeader[]
): string[] {
  const fixed = getFixedColumnPrefixIds(columnsDef);
  if (fixed.length === 0) {
    return [...order];
  }

  const fixedSet = new Set(fixed);
  const defOrder = columnsDef.map((h) => h.id);
  const seen = new Set<string>();
  const rest: string[] = [];

  for (const id of order) {
    if (fixedSet.has(id)) {
      continue;
    }
    if (!defOrder.includes(id)) {
      continue;
    }
    rest.push(id);
    seen.add(id);
  }
  for (const id of defOrder) {
    if (!fixedSet.has(id) && !seen.has(id)) {
      rest.push(id);
    }
  }
  return [...fixed, ...rest];
}

export type HeaderLayoutSegment =
  | { kind: "leaf"; header: IHeader }
  | {
      kind: "group";
      label: string | React.ReactNode;
      labelTooltip?: string;
      columns: IHeader[];
    };

export function getHeaderGroupKey(header: IHeader): string | null {
  if (header.headerGroupId) {
    return header.headerGroupId;
  }
  if (typeof header.headerGroup === "string" && header.headerGroup !== "") {
    return header.headerGroup;
  }
  return null;
}

export function hasHeaderGroup(header: IHeader): boolean {
  return getHeaderGroupKey(header) != null;
}

export function buildHeaderLayout(visibleHeaders: IHeader[]): {
  useGroupedLayout: boolean;
  segments: HeaderLayoutSegment[];
} {
  const useGroupedLayout = visibleHeaders.some(hasHeaderGroup);
  if (!useGroupedLayout) {
    return {
      useGroupedLayout: false,
      segments: visibleHeaders.map((header) => ({ kind: "leaf", header })),
    };
  }

  const segments: HeaderLayoutSegment[] = [];
  let index = 0;

  while (index < visibleHeaders.length) {
    const header = visibleHeaders[index];
    const groupKey = getHeaderGroupKey(header);

    if (!groupKey) {
      segments.push({ kind: "leaf", header });
      index += 1;
      continue;
    }

    const columns: IHeader[] = [];
    const label = header.headerGroup;
    while (index < visibleHeaders.length && getHeaderGroupKey(visibleHeaders[index]) === groupKey) {
      columns.push(visibleHeaders[index]);
      index += 1;
    }
    segments.push({
      kind: "group",
      label,
      labelTooltip: columns[0].headerGroupTooltip,
      columns,
    });
  }

  return { useGroupedLayout: true, segments };
}

export type ColumnOrderBlock = { ids: string[] };

export function buildColumnOrderBlocks(
  order: string[],
  columnsDef: IHeader[],
): ColumnOrderBlock[] {
  const headerById = new Map(columnsDef.map((h) => [h.id, h]));
  const blocks: ColumnOrderBlock[] = [];
  let index = 0;

  while (index < order.length) {
    const id = order[index];
    const header = headerById.get(id);
    if (!header) {
      index += 1;
      continue;
    }

    const groupKey = getHeaderGroupKey(header);
    if (!groupKey) {
      blocks.push({ ids: [id] });
      index += 1;
      continue;
    }

    const ids: string[] = [];
    while (index < order.length) {
      const currentHeader = headerById.get(order[index]);
      if (currentHeader && getHeaderGroupKey(currentHeader) === groupKey) {
        ids.push(order[index]);
        index += 1;
      } else {
        break;
      }
    }
    blocks.push({ ids });
  }

  return blocks;
}

/** Keeps columns that share a `headerGroup` key contiguous in column order. */
export function coalesceGroupedColumnOrder(
  order: string[],
  columnsDef: IHeader[],
): string[] {
  const headerById = new Map(columnsDef.map((h) => [h.id, h]));
  const orderIds = order.filter((id) => headerById.has(id));
  const placedGroupKeys = new Set<string>();
  const result: string[] = [];

  for (const id of orderIds) {
    const header = headerById.get(id);
    const groupKey = header ? getHeaderGroupKey(header) : null;

    if (groupKey) {
      if (placedGroupKeys.has(groupKey)) {
        continue;
      }
      placedGroupKeys.add(groupKey);
      for (const orderedId of orderIds) {
        const orderedHeader = headerById.get(orderedId);
        if (orderedHeader && getHeaderGroupKey(orderedHeader) === groupKey) {
          result.push(orderedId);
        }
      }
      continue;
    }

    result.push(id);
  }

  return normalizeColumnOrder(result, columnsDef);
}

export function moveGroupedColumnOrder(
  order: string[],
  activeId: string,
  overId: string,
  columnsDef: IHeader[],
): string[] {
  const knownIds = new Set(columnsDef.map((h) => h.id));
  const normalizedOrder = coalesceGroupedColumnOrder(
    order.filter((id) => knownIds.has(id)),
    columnsDef,
  );
  const blocks = buildColumnOrderBlocks(normalizedOrder, columnsDef);
  const oldIndex = blocks.findIndex((block) => block.ids.includes(activeId));
  const newIndex = blocks.findIndex((block) => block.ids.includes(overId));

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return normalizedOrder;
  }

  const movedBlocks = arrayMove(blocks, oldIndex, newIndex);
  return coalesceGroupedColumnOrder(
    movedBlocks.flatMap((block) => block.ids),
    columnsDef,
  );
}

export function getGroupedColumnIds(
  columnId: string,
  order: string[],
  columnsDef: IHeader[],
): string[] | undefined {
  const blocks = buildColumnOrderBlocks(
    order.filter((id) => columnsDef.some((h) => h.id === id)),
    columnsDef,
  );
  const block = blocks.find((entry) => entry.ids.includes(columnId));
  if (!block || block.ids.length <= 1) {
    return undefined;
  }
  return block.ids;
}

export function buildQueryParamsPersistentFilterOnly(
  persistentFilter: unknown
): URLSearchParams {
  const params = new URLSearchParams();
  const merged: Record<string, unknown> = {};
  if (
    persistentFilter != null &&
    typeof persistentFilter === "object" &&
    !Array.isArray(persistentFilter)
  ) {
    Object.assign(merged, persistentFilter as Record<string, unknown>);
  }
  if (Object.keys(merged).length > 0) {
    params.append("filter", JSON.stringify(merged));
  }
  return params;
}

/**
 * Generic equality/contains check used to test whether a row field's value belongs to a group.
 * Handles plain scalars, arrays (checked via `.some`), and `{ id, name }`-shaped objects, so it
 * works whether a `groupBy.additionalFields` entry holds a string, an array of strings, or an
 * array of option-like objects.
 */
export function valueMatchesGroupKey(fieldValue: unknown, groupKey: string): boolean {
  if (fieldValue == null || groupKey === "") {
    return false;
  }
  if (Array.isArray(fieldValue)) {
    return fieldValue.some((item) => valueMatchesGroupKey(item, groupKey));
  }
  if (typeof fieldValue === "object") {
    const obj = fieldValue as Record<string, unknown>;
    if (obj.name != null && String(obj.name) === groupKey) {
      return true;
    }
    if (obj.id != null && String(obj.id) === groupKey) {
      return true;
    }
    return false;
  }
  return String(fieldValue) === groupKey;
}

export type GroupedRowEntry =
  | { kind: "header"; key: string; label: React.ReactNode }
  | { kind: "row"; key: string; row: any; rowIndex: number };

function rowMatchesGroupKey(
  row: any,
  groupKey: string,
  groupBy: IGroupByConfig,
): boolean {
  if (String(row?.[groupBy.field] ?? "") === groupKey) {
    return true;
  }
  return (groupBy.additionalFields ?? []).some((fieldName) =>
    valueMatchesGroupKey(row?.[fieldName], groupKey),
  );
}

/**
 * Buckets `data` by the distinct values of `groupBy.field` (each becomes a subtitle/header
 * entry), optionally also including rows via OR-matching `groupBy.additionalFields`, so a row
 * can appear under more than one group. Groups are ordered via `groupBy.sortGroups` (default:
 * locale-aware ascending). Row order within a group preserves the original `data` order. The
 * returned `rowIndex` is a running counter across the whole flattened (possibly duplicated)
 * sequence, so `#`-column numbering stays sequential.
 */
export function buildGroupedRows(
  data: any[],
  groupBy: IGroupByConfig,
): GroupedRowEntry[] {
  const emptyGroupLabel = groupBy.emptyGroupLabel ?? "Unassigned";

  const orderedKeys: string[] = [];
  const seenKeys = new Set<string>();
  for (const row of data) {
    const key = String(row?.[groupBy.field] ?? "");
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      orderedKeys.push(key);
    }
  }

  const sortedKeys = [...orderedKeys].sort(
    groupBy.sortGroups ??
      ((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  );

  const entries: GroupedRowEntry[] = [];
  let rowIndex = 0;

  for (const key of sortedKeys) {
    const rowsForGroup = data.filter((row) => rowMatchesGroupKey(row, key, groupBy));
    if (rowsForGroup.length === 0) {
      continue;
    }

    const label =
      groupBy.getGroupLabel?.(key, rowsForGroup[0]) ??
      (key === "" ? emptyGroupLabel : key);

    entries.push({ kind: "header", key: `group-${key}`, label });

    for (const row of rowsForGroup) {
      entries.push({
        kind: "row",
        key: `group-${key}-${row?.id ?? rowIndex}`,
        row,
        rowIndex,
      });
      rowIndex += 1;
    }
  }

  return entries;
}

/**
 * Collapses `data` into a single group under one header (used when a filter targeting the same
 * dimension as `groupBy.field` is active — see `IGroupByConfig.filterName` — so rows matched via
 * an OR/`additionalFields` condition aren't scattered into their own primary-field group).
 */
export function buildSingleGroupRows(
  data: any[],
  label: React.ReactNode,
): GroupedRowEntry[] {
  if (data.length === 0) {
    return [];
  }

  const entries: GroupedRowEntry[] = [
    { kind: "header", key: `group-override-${String(label)}`, label },
  ];

  data.forEach((row, rowIndex) => {
    entries.push({
      kind: "row",
      key: `group-override-${String(label)}-${row?.id ?? rowIndex}`,
      row,
      rowIndex,
    });
  });

  return entries;
}
